export const spacing = {
  px: '1px',
  0: '0',
  0.5: '0.125rem',
  1: '0.25rem',
  1.5: '0.375rem',
  2: '0.5rem',
  2.5: '0.625rem',
  3: '0.75rem',
  3.5: '0.875rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
  14: '3.5rem',
  16: '4rem',
  20: '5rem',
  22: '5.5rem',
  24: '6rem',
  28: '7rem',
  32: '8rem',
  36: '9rem',
  40: '10rem',
  44: '11rem',
  48: '12rem',
  52: '13rem',
  56: '14rem',
  60: '15rem',
  64: '16rem',
  72: '18rem',
  80: '20rem',
  96: '24rem',
} as const;

export const colors = {
  // Brand Colors — aligned with tokens.css
  brand: {
    primary: '#00625B', // --pmx-deep-teal
    secondary: '#F8EFE5', // accent fill
    accent: '#A48163', // --pmx-tan
    text: '#152319', // text primary
    error: '#B42318', // --pmx-alert
  },

  // Semantic Colors — aligned with tokens.css
  semantic: {
    error: '#B42318', // --pmx-alert
    success: '#067647', // success
    warning: '#B54708', // warning
    info: '#175CD3', // info
  },

  // Neutral Colors — aligned with tokens.css gray scale
  neutral: {
    50: '#FCFCFD', // --pmx-bg-surface
    100: '#EEEEEE', // --pmx-gray-100
    200: '#E6E6E6', // --pmx-gray-200
    300: '#D0D5DD', // --pmx-gray-300
    400: '#98A2B3', // --pmx-gray-400
    500: '#838D86', // text tertiary
    600: '#838D86', // alias
    700: '#3E4A42', // text secondary
    800: '#152319', // text primary
    900: '#101828', // --pmx-gray-900
  },

  // Background Colors — aligned with tokens.css
  background: {
    primary: '#FAF6F1', // --pmx-bg-page
    secondary: '#FCFCFD', // --pmx-bg-surface
    tertiary: '#F8EFE5', // accent fill
  },

  // PMX Colors
  pmx: {
    green: {
      1: '#00625B', // --pmx-deep-teal
      2: '#003E3A',
      3: '#C9DFBB',
      4: '#152319', // text primary
    },
    accent: {
      1: '#DFF9BA',
      2: '#C7E6EB',
      3: '#CEDAEF',
      4: '#CEC7EB',
      5: '#F8EFE5', // accent fill
      6: '#DFBBBB',
      7: '#FFFFFF',
      8: '#939598',
    },
    grey: {
      50: '#F5F7FA',
      100: '#E4E7EB',
      200: '#CBD2D9',
      300: '#9AA5B1',
      400: '#7B8794',
      500: '#616E7C',
      600: '#52606D',
      700: '#3E4C59',
      800: '#323F4B',
      900: '#1F2933',
    },
    teal: {
      50: '#F0FCF9',
      100: '#C6F7E9',
      200: '#8EEDD1',
      300: '#5FE3C0',
      400: '#2DCCA7',
      500: '#17B897',
      600: '#079A82',
      700: '#048271',
      800: '#016457',
      900: '#004440',
    },
    yellow: {
      50: '#FFFBEA',
      100: '#FFF3C4',
      200: '#FCE588',
      300: '#FADB5F',
      400: '#F7C948',
      500: '#F0B429',
      600: '#DE911D',
      700: '#CB6E17',
      800: '#B44D12',
      900: '#8D2B0B',
    },
    red: {
      50: '#FFE3E3',
      100: '#FFBDBD',
      200: '#FF9B9B',
      300: '#F86A6A',
      400: '#EF4E4E',
      500: '#E12D39',
      600: '#CF1124',
      700: '#AB091E',
      800: '#8A041A',
      900: '#610316',
    },
  },
} as const;

export const typography = {
  fonts: {
    primary: '"Geist", "Inter", system-ui, sans-serif',
    secondary: '"Inter", system-ui, sans-serif',
    mono: '"Geist Mono", ui-monospace, "SF Mono", Menlo, Consolas, monospace', // aligned with tokens.css
  },
  sizes: {
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.875rem',
    '4xl': '2.25rem',
    '5xl': '3rem',
    '6xl': '3.75rem',
    '7xl': '4.5rem',
    '8xl': '6rem',
    '9xl': '8rem',
  },
  weights: {
    thin: '100',
    extralight: '200',
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
    black: '900',
  },
  lineHeights: {
    none: '1',
    tight: '1.25',
    snug: '1.375',
    normal: '1.5',
    relaxed: '1.625',
    loose: '2',
  },
} as const;

// Shadows — aligned with tokens.css
export const shadows = {
  sm: '0 1px 2px rgba(16,24,40,0.06)', // --pmx-shadow-card
  base: '0 4px 6px -2px rgba(16,24,40,0.03)', // --pmx-shadow-float
  md: '0 4px 6px -2px rgba(16,24,40,0.03)', // --pmx-shadow-float
  lg: '0 8px 8px -4px rgba(16,24,40,0.03)', // --pmx-shadow-pop
  xl: '0 24px 48px -12px rgba(16,24,40,0.18)', // --pmx-shadow-modal
  '2xl': '0 24px 48px -12px rgba(16,24,40,0.22)',
  inner: 'inset 0 2px 4px 0 rgba(16,24,40,0.05)',
} as const;

// Radii — aligned with tokens.css
export const radii = {
  none: '0',
  xs: '2px', // --pmx-radius-xs
  sm: '4px', // --pmx-radius-sm
  base: '8px', // --pmx-radius-md
  md: '8px', // --pmx-radius-md
  lg: '12px', // --pmx-radius-lg
  xl: '16px', // --pmx-radius-xl
  '2xl': '24px', // --pmx-radius-2xl
  '3xl': '24px', // capped at 24px in DS v2.0
  full: '9999px', // --pmx-radius-pill
} as const;

export const breakpoints = {
  xs: '320px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
} as const;

export const zIndices = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
} as const;

export const animations = {
  keyframes: {
    slideIn: {
      '0%': { transform: 'translateX(100%)', opacity: '0' },
      '100%': { transform: 'translateX(0)', opacity: '1' },
    },
    slideInFromLeft: {
      '0%': { transform: 'scaleX(0)', transformOrigin: 'left' },
      '100%': { transform: 'scaleX(1)', transformOrigin: 'left' },
    },
    'fade-in': {
      '0%': { opacity: '0', transform: 'translateY(-10px)' },
      '100%': { opacity: '1', transform: 'translateY(0)' },
    },
  },
  durations: {
    slideIn: '0.3s',
    slideInFromLeft: '0.3s',
    'fade-in': '0.3s',
  },
  timingFunctions: {
    easeOut: 'ease-out',
  },
} as const;

export const theme = {
  colors,
  typography,
  spacing,
  shadows,
  radii,
  breakpoints,
  zIndices,
  animations,
} as const;

export type Theme = typeof theme;
